'use client';

import { forwardRef } from 'react';
import type { ShopCardProps } from '@paypoteam/page-components/ShopCard';
import { ShopCard } from '@paypoteam/page-components/ShopCard';
import { buttonVariants } from '@paypoteam/ui-components/Button';
import { P } from '@paypoteam/ui-components/P';
import { Title } from '@paypoteam/ui-components/Title';
import type { ImageProps } from 'next/image';
import Image from 'next/image';

import { shopReferral } from '@/lib/gtm';

import H2 from '@/components/atoms/Typography/H2';
import { ItemsContainer } from '@/components/layout/ItemsContainer';
import Section from '@/components/layout/Section';
import type { ShopCardsSectionProps } from '@/components/sections/ShopCardsSection/ShopCardsSection.types';

const ShopCardsSection = forwardRef<HTMLDivElement, ShopCardsSectionProps>(
    ({ id, config, partners, shopLinkPlacement, ...props }, ref) => {
        const dataTestId =
            props['data-test-id'] && config?.title
                ? `${props['data-test-id']}-ShopCardsSection-${(config?.title || shopLinkPlacement || '').replaceAll(' ', '-')}`
                : undefined;

        return (
            <Section
                ref={ref}
                id={id || config?.id}
                {...props}
            >
                {!!config?.title && (
                    <H2
                        className="heading-spaces text-center"
                        data-test-id={dataTestId && `${dataTestId}-title`}
                    >
                        {config.title}
                    </H2>
                )}

                {!!config?.subtitle && (
                    <Title
                        size="m"
                        className="mb-6 text-center md:mb-8"
                        dangerouslySetInnerHTML={{ __html: config.subtitle }}
                    />
                )}

                {!!config?.smallText && (
                    <P
                        size="2xs"
                        weight="regular"
                        className="mb-2 text-left font-primary leading-5 text-grey-600"
                        dangerouslySetInnerHTML={{ __html: config.smallText }}
                    />
                )}

                {partners && (
                    <ItemsContainer
                        gridOptions={{ columns: 5, gap: 'custom' }}
                        className="gap-x-2 gap-y-[14px] sm:gap-x-3 sm:gap-y-[18px] md:gap-x-6 md:gap-y-[30px]"
                        data-test-id={dataTestId}
                    >
                        {partners.map(
                            ({ partnerName, ...elem }) =>
                                !!elem && (
                                    <ShopCard<ImageProps>
                                        {...(elem as ShopCardProps<ImageProps>)}
                                        key={elem.href}
                                        partnerName={partnerName}
                                        className="min-h-[12.25rem] focus:outline-offset-4 md:min-h-[16rem]"
                                        ImageComponent={Image}
                                        onClick={() => {
                                            shopReferral(
                                                partnerName,
                                                shopLinkPlacement!,
                                            );
                                        }}
                                    />
                                ),
                        )}
                    </ItemsContainer>
                )}

                {config?.cta &&
                    !!Object.keys(config.cta).length &&
                    config.cta?.children && (
                        <a
                            href={config.cta.href}
                            target="_blank"
                            data-test-id={config.cta.dataTestId}
                            className={buttonVariants({
                                variant: 'secondary',
                                className:
                                    'mx-auto my-0 mt-8 w-full !whitespace-normal px-6 text-center md:w-fit',
                            })}
                        >
                            {config.cta.children}
                        </a>
                    )}
            </Section>
        );
    },
);

export default ShopCardsSection;
